import { createFileRoute, Link } from '@tanstack/react-router'
import { useUserMarket } from '@/lib/i18n'
import {
  getReportingChannels,
  getCrisisLine,
} from '@/lib/emergency-contacts'
import { FiArrowLeft, FiPrinter } from 'react-icons/fi'

export const Route = createFileRoute('/_auth/toolkit')({
  component: ToolkitPage,
})

/** Inline fill line: label on the left, ruled line on the right. */
function InlineField({ label, labelWidth = 110 }: { label: string; labelWidth?: number }) {
  return (
    <div className="flex items-end gap-3">
      <span
        className="text-sm text-slate-500 shrink-0"
        style={{ minWidth: labelWidth }}
      >
        {label}
      </span>
      <span className="flex-1 h-7 border-b border-slate-300" />
    </div>
  )
}

/** Stacked fill line: small label sitting above the ruled line. Use when columns are tight. */
function StackedField({ label }: { label: string }) {
  return (
    <div>
      <span className="block text-xs text-slate-500">{label}</span>
      <span className="block mt-1 h-7 border-b border-slate-300" />
    </div>
  )
}

function SectionHeading({
  title,
  subtitle,
}: {
  title: string
  subtitle?: string
}) {
  return (
    <>
      <h3 className="text-base sm:text-lg font-semibold text-slate-900">{title}</h3>
      {subtitle && <p className="mt-0.5 text-sm text-slate-500 leading-snug">{subtitle}</p>}
    </>
  )
}

function ToolkitPage() {
  const userMarket = useUserMarket()
  const channels = getReportingChannels(userMarket)
  const crisis = getCrisisLine(userMarket)

  return (
    <div className="min-h-screen bg-[#f5f5f7] print:bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-4 pb-16 print:px-0 print:pt-0 print:pb-0">
        {/* Top bar (hidden in print) */}
        <div className="flex items-center justify-between gap-3 print:hidden">
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800 transition-colors"
          >
            <FiArrowLeft className="h-4 w-4" />
            Home
          </Link>
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-1.5 rounded-full bg-violet-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-violet-700 cursor-pointer"
          >
            <FiPrinter className="h-4 w-4" />
            Print my card
          </button>
        </div>

        {/* Lead-in copy (screen only) */}
        <div className="mt-6 print:hidden">
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight text-slate-900 leading-tight">
            For your fridge
          </h1>
          <p className="mt-2 text-base text-slate-500 max-w-xl leading-relaxed">
            Print it once and keep it somewhere obvious. The numbers
            for your country come pre-filled; you write in the rest by hand.
          </p>
        </div>

        {/* The printable card */}
        <article className="mt-6 print:mt-0 bg-white print:bg-white rounded-2xl print:rounded-none border border-slate-200 print:border-0 shadow-[0_4px_16px_rgba(15,23,42,0.04)] print:shadow-none px-6 py-7 sm:px-9 sm:py-9 print:px-8 print:py-6">
          <header>
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight">
              Emergency card
            </h2>
          </header>

          {/* Authorities + action checklist — side-by-side */}
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-6">
            <section>
              <SectionHeading title="Where to report a scam" />
              <ul className="mt-3 space-y-1.5">
                {channels.map((c) => (
                  <li key={c.org} className="text-sm text-slate-700 leading-relaxed">
                    <span className="font-semibold text-slate-900">{c.org}:</span>{' '}
                    {c.detail}
                  </li>
                ))}
                <li className="text-sm text-slate-700 leading-relaxed">
                  <span className="font-semibold text-slate-900">{crisis.org}:</span>{' '}
                  {crisis.detail}
                </li>
              </ul>
            </section>

            <section>
              <SectionHeading title="First steps" />
              <ol className="mt-3 space-y-1.5 list-decimal list-inside text-sm text-slate-700 leading-relaxed marker:text-slate-400 marker:font-semibold">
                <li>End the conversation.</li>
                <li>Call your bank if money moved.</li>
                <li>Change passwords, starting with email.</li>
                <li>Save screenshots and receipts.</li>
                <li>Report it. Talk about it.</li>
              </ol>
            </section>
          </div>

          {/* Your bank — fillable */}
          <section className="mt-6">
            <SectionHeading
              title="Your bank"
              subtitle="From the back of your card or your bank’s app."
            />
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-4 gap-y-3">
              <StackedField label="Bank name" />
              <StackedField label="Fraud line" />
            </div>
          </section>

          {/* Your cards — fillable, up to 3 */}
          <section className="mt-6">
            <SectionHeading
              title="Your cards"
              subtitle="Label and the phone number on the back. Don’t write the card number."
            />
            <div className="mt-3 space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-x-4 gap-y-3"
                >
                  <StackedField label={`Card ${i} label`} />
                  <StackedField label="Phone" />
                </div>
              ))}
            </div>
          </section>

          {/* Trusted contacts — fillable, up to 3, stacked layout to fit 3 columns */}
          <section className="mt-6">
            <SectionHeading
              title="Trusted contacts"
              subtitle="People you’ll call before doing anything you’re unsure about."
            />
            <div className="mt-3 space-y-3">
              {[1, 2, 3].map((i) => (
                <div
                  key={i}
                  className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.1fr)_minmax(0,1fr)_minmax(0,0.9fr)] gap-x-4 gap-y-3"
                >
                  <StackedField label="Name" />
                  <StackedField label="Phone" />
                  <StackedField label="Relationship" />
                </div>
              ))}
            </div>
          </section>

          {/* Family password — instructions only */}
          <section className="mt-6">
            <SectionHeading
              title="Family emergency password"
              subtitle="A short phrase only your real family knows. If a panicked call doesn’t use it, it’s probably not them. Write it here on the printed copy only."
            />
            <div className="mt-3">
              <InlineField label="Phrase" />
            </div>
          </section>

        </article>
      </div>

      {/* Print stylesheet */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 12mm 14mm;
          }
          html, body {
            background: white !important;
          }
        }
      `}</style>
    </div>
  )
}
